import { IDefaultResponse } from '@models/interfaces/global.interface';
import { DetailUser } from '@models/types/users.type';

export interface IUserLoginReq {
  username: string;
  multiple_account: boolean;
  password: string;
  remember_me: boolean;
  user_type?: string | number;
}
export interface IUserForgotPasswordReq {
  username: string;
  user_type: number | 1 | 2 | 3;
}
export interface IUserForgotPasswordRes {
  error: boolean;
  error_code: string;
  error_msg: string;
  msg?: string;
}

export interface IUserData {
  user_type: number;
  user_id: string;
  user_login: string;
  country_code: string;
  user_mobile: string;
  mobile_verified: string;
  first_name: string;
  last_name: string;
  email_address: string;
  contact: string;
  parent_bubble: string;
  dob: string;
  gender: string;
  level_id: string;
  date_created: string;
  user_uid: string;
  access_token: string;
  expired_at: number;
  session_lifetime: number;
  pp: string;
  last_login_date?: string;
}

export interface IUserResetPasswordReq {
  new_password: string;
  confirm_password: string;
  reset_token: string;
  logout_other_devices: number;
}
export interface IUserResetPasswordRes {
  error: boolean;
  error_code: string;
  error_msg: string;
  msg?: string;
}

export interface IUserResetPasswordTokenReq {
  reset_token: string;
}
export interface IUserResetPasswordTokenRes {
  error: boolean;
  error_code: string;
  error_msg: string;
}

export interface ILoginRes extends IDefaultResponse {
  data: IUserData[];
}

export interface IGetStudentOnboardingTooltipPermissionRes
  extends IDefaultResponse {
  data: {
    is_show_onboarding_tooltip: boolean;
  };
}

export interface IGetStudentOnboardingMasteryLevelPermissionRes
  extends IDefaultResponse {
  data: {
    is_show_onboarding_mastery_level: boolean;
  };
}

export interface IGetOtpCodeFormData {
  country_code: string;
  mobile: string;
  is_from_new_signup_flow?: number;
}

export interface IVerifyOtpCodeFormData {
  country_code: string;
  mobile: string;
  otp_code: string;
  is_from_new_signup_flow?: number;
}

export interface IGetOtpCodeRes extends IDefaultResponse {
  expired_date: string;
}

export interface IGoToBubbleStoreRes {
  error: boolean;
  error_message: string;
  bubbly_uid: string;
  bubbly_url: string;
}

export interface ISubjectSubscription {
  subject_id: number;
  subject: string;
  code: string;
  color: string;
  display_name: string;
  subscription_type: string;
  premium_plan: string;
  online_lesson_access: string;
  past_lesson_access: string;
  genie_credit?: number;
  master_genie_credit?: number;
  expiry_date?: string;
  plan_description?: string;
  subscription_plan_id?: number;
  subscription_grace_period: number;
  solution_explanation: number;
  genieask_state: number;
  has_genieask?: string; // stringified number
  has_geniesmart?: string; // stringified number
  auto_renewal: boolean;
  renewal_credit: string;
  is_has_geniesmart: boolean;
  is_has_genieclass: boolean;
  is_has_genieask: boolean;
  is_trial_plan?: boolean;
  billing_frequency: number;
  billing_period: string;
  currency_code: string;
  amount: string;
  delayed_start_date: string;
}

export interface IGetSubjectSubscriptionRes extends IDefaultResponse {
  data: ISubjectSubscription[];
}

export interface IMarkReadNotifRes extends IDefaultResponse {
  data?: boolean;
}

export interface IUserUnreadNotificationRes extends IDefaultResponse {
  data?: number;
}

export interface IBadgeNotification {
  badge_level_id: number;
  badge_id: number;
  level: string;
}

export interface INotificationItem {
  badges: IBadgeNotification[];
  channel_id: number;
  class_name: string;
  date_clicked: string | null;
  date_display: string;
  date_read: string;
  date_time: string;
  is_boosted: boolean;
  is_eligible_for_boost: boolean;
  level_id: number;
  message_id: number;
  multiple_badges: number;
  notification_action: number;
  notification_date: string;
  notification_id: number;
  notification_text: string;
  notification_title: string;
  notification_type: string;
  online_lesson_id: number;
  part_id: string;
  progress_report_id: number;
  streak_type: number;
  student_exam_score_id: number;
  student_id: number;
  study_plan_id: number;
  study_plan_week_id: number;
  subject_id: number;
  topic_id: number;
  url: string;
  value_1: string;
  worksheet_id: number;
}

export interface IGetNotificationsRes extends IDefaultResponse {
  data: INotificationItem[];
}

export interface IResetPasswordRes extends IDefaultResponse {
  msg?: string;
}

export interface IForgotPasswordRes extends IDefaultResponse {
  msg: string;
}

export interface IUserDetailRes extends IDefaultResponse {
  data: DetailUser;
}

export interface IAccountProfileData {
  email: string;
  mobile_number: number;
  country_code: string | number;
}

export type genderType = '1' | '2';
export type showDateOfBirth = 0 | 1;
export type showSchool = 0 | 1;

export interface IBasicInfoData {
  date_of_birth: string;
  show_date_of_birth: showDateOfBirth;
  gender: genderType;
}

export interface IFormAcademicData {
  school_id: number;
  show_school: showSchool;
  level_id: number;
  class: string;
}

export interface IFormPasswordData {
  current_password: string;
  new_password: string;
  password_confirmation: string;
}

export interface ISchoolDetails {
  school_id: number | string;
  school_detail_id: number | string;
  school_name: string;
  school_code: string | null;
  school_program_id: string;
  government_involvement_id: string;
  status: '1' | '0';
  date_created: string;
  date_updated: string;
}

export interface ITopUpSelection {
  subscription_topup_id: number;
  genie_credit: number;
  price: number;
  currency_code: string;
  gst_rate: number;
  gst_amount: number;
}

export interface IGetTopUpGenieCreditList extends IDefaultResponse {
  data: ITopUpSelection[];
}

export interface ITopUpWorksheetCreditReq {
  subject_id: number;
  subscription_topup_id: number;
  finish_url: string;
  student_id?: string;
}

export interface ITopUpWorksheetCreditResp extends IDefaultResponse {
  data: {
    redirect_url: string;
    payment_gateway_transaction_id: string;
  };
}

export enum SubscriptionStatus {
  newUser = 1,
  activeAutoRenewOn = 2,
  activeAutoRenewOff = 3,
  activeNoAutoRenew = 4,
  subscriptionFailedChargeStillActive = 5,
  subscriptionFailedChargeExpired = 6,
  subscriptionExpired = 7,
  subscriptionTrial = 8,
}

export enum SubscriptionSource {
  android = 1,
  ios = 2,
  web = 3,
}

export interface IPlanOverviewDetail {
  country_id: number;
  subscription_id: number | null;
  subscription_plan_id: number;
  subscription_plan_name: string;
  worksheet_credit: number;
  bubbles_parent: number;
  is_allow_gc: 0 | 1;
  is_allow_ga: 0 | 1;
  billing_frequency: number;
  billing_period: string;
  price: number;
  gst_rate?: number | string;
  price_monthly: number;
  is_refundable: number;
  recurring: {
    recurring_date: string | null;
    recurring_amount: string | null;
  };
  subscription_status: SubscriptionStatus;
  subscription_source: SubscriptionSource;
  currency_code: string;
  worksheet_recharge_date?: string;
}

export interface IPlanOverviewDetailReq {
  subject_id?: number;
  is_chinese?: 0 | 1;
}

export interface IPlanOverviewDetailRes extends IDefaultResponse {
  data: IPlanOverviewDetail;
}

export interface IUpdateRenewalReq {
  subscription_id: number;
  auto_renewal_new_status: 0 | 1;
  remark?: string;
}

export interface IRequestPaymentLinkRes extends IDefaultResponse {
  data: {
    payment_link: string;
    is_new_invoice: boolean;
    subscription_invoice_id: number;
  };
}

export interface ICheckInvoiceStatusReq {
  subject_id: number;
  subscription_invoice_id: number;
}

export enum InvoiceStatus {
  splashScreen = 0,
  registration = 1,
  subscriptionAgreement = 2,
  payment = 3,
  completed = 4,
  failed = 5,
  pending = 6,
  permanentlyClosed = 7,
  optionalRecurring = 8,
}

export interface ICheckInvoiceStatusRes extends IDefaultResponse {
  invoice_status: InvoiceStatus;
}

export interface IExamType {
  exam_type_id: number;
  exam_name: string;
}

export interface IGetExamTypeRes extends IDefaultResponse {
  data: IExamType[];
}

export interface IExamResultFormData {
  exam_score_id?: string | null;
  subject_id?: number;
  level_id?: string;
  subject_name?: string;
  exam_date?: string;
  exam_type_id?: string;
  input_exam_type_others?: string;
  marks?: number;
  max_marks?: number;
  exam_grade_id?: string;
  input_exam_grade_na?: string;
  image?: File[];
}

export interface ICheckStudentHasSubmittedExamResult {
  exam_score_id?: string;
  level_id?: number;
  subject_id?: number;
  exam_type_id?: string;
  exam_date?: string;
}

export interface ISubmitExamResultData extends IDefaultResponse {
  student_exam_score_id: number;
}

export interface IExistingExamResultData {
  student_exam_score_id?: number;
  student_exam_image_ids?: string[];
}

export interface ICheckStudentHasSubmittedExamResultRes
  extends IDefaultResponse {
  data: IExistingExamResultData;
}

export interface IDeleteExamResult {
  student_exam_score_id: number;
}

export interface IDeleteExamResultAttachment {
  exam_image_id: string;
  subject_id: number;
}
